import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import {
  LayoutDashboard, Cpu, Package, CalendarClock, ClipboardList,
  History, Users, Bell, Settings, BarChart2, KeyRound, CreditCard,
} from 'lucide-react';

type Role = 'admin' | 'supervisor' | 'technician';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles: readonly Role[];
}

interface NavGroup {
  heading: string;
  items: readonly NavItem[];
}

const ALL: readonly Role[] = ['admin', 'supervisor', 'technician'];

const navGroups: readonly NavGroup[] = [
  {
    heading: 'Overview',
    items: [
      { to: '/dashboard',     label: 'Dashboard',     icon: LayoutDashboard, roles: ALL },
      { to: '/notifications', label: 'Notifications', icon: Bell,            roles: ALL },
    ],
  },
  {
    heading: 'Assets',
    items: [
      { to: '/equipment', label: 'Equipment', icon: Cpu,     roles: ALL },
      { to: '/parts',     label: 'Parts',     icon: Package, roles: ALL },
    ],
  },
  {
    heading: 'Work',
    items: [
      { to: '/work-orders', label: 'Work Orders', icon: ClipboardList, roles: ALL },
      { to: '/schedules',   label: 'Schedules',   icon: CalendarClock, roles: ALL },
      { to: '/history',     label: 'History',     icon: History,       roles: ALL },
    ],
  },
  {
    heading: 'Insights',
    items: [
      { to: '/reports', label: 'Reports', icon: BarChart2, roles: ['admin', 'supervisor'] },
    ],
  },
  {
    heading: 'Admin',
    items: [
      { to: '/users',            label: 'Users',     icon: Users,      roles: ['admin'] },
      { to: '/settings/license', label: 'License',   icon: KeyRound,   roles: ['admin'] },
      { to: '/settings/billing', label: 'Billing',   icon: CreditCard, roles: ['admin'] },
      { to: '/audit',            label: 'Audit Log', icon: Settings,   roles: ['admin', 'supervisor'] },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const role = (user?.role ?? 'technician') as Role;

  return (
    <aside
      style={{ background: '#11151f' }}
      className={cn(
      'w-56 flex-shrink-0 text-slate-100 flex flex-col',
      // Mobile: fixed panel sliding in from left, starts below the 56px topbar
      'fixed top-14 bottom-0 left-0 z-40 transition-transform duration-200 ease-in-out',
      isOpen ? 'translate-x-0' : '-translate-x-full',
      // Desktop: static sidebar, always visible, full height
      'sm:static sm:top-0 sm:bottom-auto sm:z-auto sm:translate-x-0 sm:h-auto',
    )}>
      {/* Logo — hidden on mobile (sidebar starts below topbar) */}
      <div className="h-24 hidden sm:flex items-center justify-center px-3 border-b border-white/5">
        <img src="/logo.png?v=4" alt="VantixMT" className="h-20 w-auto object-contain" />
      </div>

      {/* Nav — grouped by section */}
      <nav className="flex-1 overflow-y-auto py-2">
        {navGroups.map(group => {
          const items = group.items.filter(item => item.roles.includes(role));
          if (items.length === 0) return null;
          return (
            <div key={group.heading} className="mb-1">
              <p className="px-5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                {group.heading}
              </p>
              {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onClose}
                  className={({ isActive }) => cn(
                    'mx-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-brand-600 text-white font-medium'
                      : 'text-slate-300 hover:bg-white/5 hover:text-white',
                  )}
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* User info */}
      <div className="border-t border-white/5 px-5 py-3">
        <p className="text-xs text-slate-400 truncate">{user?.email}</p>
        <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
      </div>
    </aside>
  );
}
